/**
 * 全屏工具 —— 兼容 Safari 老前缀 + Android Chrome
 *
 * 用法：toggleFullscreen() 一键切换；isFullscreen() 查询当前状态。
 *
 * 注意：必须在用户手势事件（点击 / 触摸）回调里同步调用，
 * 否则浏览器会拒绝。所以按钮的 click handler 直接调即可，
 * 不能放到 setTimeout / async 后再调。
 */

interface VendorElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
}
interface VendorDocument extends Document {
  webkitFullscreenElement?: Element | null;
  msFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
  msExitFullscreen?: () => Promise<void>;
}

export function isFullscreen(): boolean {
  const d = document as VendorDocument;
  return !!(d.fullscreenElement || d.webkitFullscreenElement || d.msFullscreenElement);
}

export function toggleFullscreen(target: HTMLElement = document.documentElement): void {
  if (isFullscreen()) {
    void exitFullscreen();
  } else {
    void enterFullscreen(target);
  }
}

export async function enterFullscreen(target: HTMLElement = document.documentElement): Promise<void> {
  const el = target as VendorElement;
  try {
    if (el.requestFullscreen) await el.requestFullscreen();
    else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
    else if (el.msRequestFullscreen) await el.msRequestFullscreen();
  } catch {
    // 用户拒绝 / 浏览器不支持 —— 静默失败
  }
}

export async function exitFullscreen(): Promise<void> {
  const d = document as VendorDocument;
  try {
    if (d.exitFullscreen) await d.exitFullscreen();
    else if (d.webkitExitFullscreen) await d.webkitExitFullscreen();
    else if (d.msExitFullscreen) await d.msExitFullscreen();
  } catch {
    /* ignore */
  }
}

/** 浏览器是否支持全屏 API（用来决定要不要显示按钮） */
export function isFullscreenSupported(): boolean {
  const el = document.documentElement as VendorElement;
  return !!(el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen);
}
